/** Resolution engagement (Case) domain types. Mirrors the backend
 * `apps/cases` read shape, remapped to camelCase by `api.ts`. */

export type ResolutionType =
  | "IA"
  | "OIC"
  | "CNC"
  | "INNOCENT_SPOUSE"
  | "PENALTY_ABATEMENT"
  | "AUDIT"
  | "EXCISE"
  | "OTHER";

export type CaseStatus =
  | "PROSPECT"
  | "ACTIVE"
  | "PENDING_IRS"
  | "ON_HOLD"
  | "RESOLVED"
  | "CLOSED";

/** Statuses that count as "done" (a case is no longer open). */
export const DONE_STATUSES: ReadonlySet<CaseStatus> = new Set(["RESOLVED", "CLOSED"]);

export function isCaseDone(status: CaseStatus): boolean {
  return DONE_STATUSES.has(status);
}

export interface CaseStep {
  id: string;
  description: string;
  position: number;
  dueDate: string | null;
  isCompleted: boolean;
  completedAt: string | null;
}

export interface Case {
  id: string;
  client: string;
  clientName: string;
  assignedPractitioner: string | null;
  assignedPractitionerName: string;
  resolutionType: ResolutionType;
  resolutionTypeDisplay: string;
  status: CaseStatus;
  statusDisplay: string;
  balanceCents: number;
  taxYears: string;
  openedOn: string;
  targetDate: string | null;
  followupDate: string | null;
  followupDesc: string;
  /** Earliest of follow-up/target date (ISO), or null if neither is set. */
  nextDue: string | null;
  /** Signed days from today to `nextDue` (negative = overdue), or null. */
  daysUntilDue: number | null;
  closedAt: string | null;
  notes: string;
  steps: CaseStep[];
  openStepCount: number;
  createdAt: string;
  updatedAt: string;
}

/** An entry in a case's notes / activity log. */
export interface CaseNote {
  id: string;
  text: string;
  isPinned: boolean;
  /** Read-only audit entry (case opened, status changed, step completed). */
  isSystem: boolean;
  author: number | null;
  authorName: string;
  createdAt: string;
  updatedAt: string;
}

export interface CaseNotePayload {
  text?: string;
  is_pinned?: boolean;
}

/** Case-list filter tabs (§3). Maps to `status`/`due` query params. */
export type CaseFilter = "open" | "today" | "week" | "completed" | "all";

export const CASE_FILTERS: { value: CaseFilter; label: string }[] = [
  { value: "open", label: "Open" },
  { value: "today", label: "Due Today" },
  { value: "week", label: "Due This Week" },
  { value: "completed", label: "Completed" },
  { value: "all", label: "All" },
];

/** Write payload (snake_case, on the wire). `client` is set only on create. */
export interface CasePayload {
  client?: string;
  assigned_practitioner?: string | null;
  resolution_type: ResolutionType;
  status: CaseStatus;
  balance: string;
  tax_years: string;
  target_date?: string | null;
  followup_date?: string | null;
  followup_desc?: string;
  notes?: string;
}

export interface CaseStepPayload {
  description?: string;
  due_date?: string | null;
  is_completed?: boolean;
  position?: number;
}

export const RESOLUTION_TYPE_OPTIONS: { value: ResolutionType; label: string }[] = [
  { value: "IA", label: "Installment Agreement" },
  { value: "OIC", label: "Offer in Compromise" },
  { value: "CNC", label: "Currently Non-Collectible" },
  { value: "INNOCENT_SPOUSE", label: "Innocent Spouse Relief" },
  { value: "PENALTY_ABATEMENT", label: "Penalty Abatement" },
  { value: "AUDIT", label: "Audit Representation" },
  { value: "EXCISE", label: "Excise Tax / Trust Fund Recovery (Form 4180)" },
  { value: "OTHER", label: "Other" },
];

export const CASE_STATUS_OPTIONS: { value: CaseStatus; label: string }[] = [
  { value: "PROSPECT", label: "Prospect" },
  { value: "ACTIVE", label: "Active" },
  { value: "PENDING_IRS", label: "Pending IRS" },
  { value: "ON_HOLD", label: "On Hold" },
  { value: "RESOLVED", label: "Resolved" },
  { value: "CLOSED", label: "Closed" },
];
